import { MvnMessage } from "@newLibs/utils";
import { DocumentSignerSignatureExt, StampImageSignature } from "./types";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { DocsPdfDocument, DocumentContentType, PdfDocument } from "@maven-rest/docs/Document.dto";
import { DocumentSign, DocumentSigner, DocumentSignType } from "@maven-rest/docs/DocumentSign.dto";
import { BrowseEntryType, DocumentEntry, FolderType } from "@maven-rest/docs/Folder.dto";
import { FolderFileType } from "@maven-rest/docs/FolderFile.dto";
import { FolderSharingRole, FolderSharingRoleBy } from "@maven-rest/docs/FolderSharing.dto";

export function getInitialDocumentFile(
  documentSigner: DocumentSigner,
  documentSign: DocumentSign
): DocumentEntry<PdfDocument | DocsPdfDocument> & {
  role: FolderSharingRole;
  roleBy: FolderSharingRoleBy;
} {
  return {
    type: BrowseEntryType.Document,
    resource: {
      id: documentSign.fileId,
      organizationId: BigInt(0),
      folderId: BigInt(0),
      type: FolderFileType.Document,
      folderType: documentSign.type === DocumentSignType.EConsent ? FolderType.ECONSENT : FolderType.NORMAL,
      name: "",
      size: 0,
      createdBy: {
        id: BigInt(0),
        name: "",
        email: "",
      },
      createdAt: new Date(),
      isDestroyed: false,
    },
    document: {
      id: BigInt(0),
      title: "",
      version: "",
      ...(documentSign.docsPdfId
        ? { contentType: DocumentContentType.DocsPdf, docsPdfId: documentSign.docsPdfId, metainfo: documentSign.metainfo }
        : { contentType: DocumentContentType.PDF, contentUrl: documentSigner.contentUrl }),
    },
    role: FolderSharingRole.Viewer,
    roleBy: {
      folderId: null as unknown as bigint,
      fileId: null as unknown as bigint,
    },
    isManager: false,
  };
}

export function getSignatureImageBase64(documentSignerSignature?: DocumentSignerSignatureExt): string {
  if (!documentSignerSignature || !documentSignerSignature.signature) return "";

  const { signature } = documentSignerSignature;

  if (isStampImageSignature(signature)) {
    return signature.stampBase64;
  }

  return signature;
}

export const isStampImageSignature = (sign: DocumentSignerSignatureExt["signature"]): sign is StampImageSignature => {
  return typeof sign === "object" && "key" in sign && "bucket" in sign && "stampBase64" in sign;
};

export function getSignReasonMessages(documentSign: DocumentSign, signerIndex: number): Array<{ key: string; text: string }> {
  return documentSign.type === DocumentSignType.EConsent
    ? signerIndex === 1
      ? [
          {
            key: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ECONSENT_REASON_APPROVER_SIGNER),
            text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ECONSENT_REASON_APPROVER_SIGNER),
          },
          {
            key: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
            text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
          },
        ]
      : [
          {
            key: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ECONSENT_REASON_APPROVER_INVESTIGATOR),
            text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ECONSENT_REASON_APPROVER_INVESTIGATOR),
          },
          {
            key: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
            text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
          },
        ]
    : [
        {
          key: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_APPROVER),
          text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_APPROVER),
        },
        {
          key: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_REVIEWER),
          text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_REVIEWER),
        },
        {
          key: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
          text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
        },
      ];
}

export function getSignReasonMessagesForGrSelect(documentSign: DocumentSign, signerIndex: number): Array<{ label: string; value: string }> {
  return documentSign.type === DocumentSignType.EConsent
    ? signerIndex === 1
      ? [
          {
            label: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ECONSENT_REASON_APPROVER_SIGNER),
            value: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ECONSENT_REASON_APPROVER_SIGNER),
          },
          {
            label: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
            value: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
          },
        ]
      : [
          {
            label: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ECONSENT_REASON_APPROVER_INVESTIGATOR),
            value: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ECONSENT_REASON_APPROVER_INVESTIGATOR),
          },
          {
            label: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
            value: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
          },
        ]
    : [
        {
          label: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_APPROVER),
          value: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_APPROVER),
        },
        {
          label: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_REVIEWER),
          value: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_REVIEWER),
        },
        {
          label: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_CONFIRM_WRITE),
          value: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_CONFIRM_WRITE),
        },
        {
          label: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
          value: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER),
        },
      ];
}
